import React from "react";
import { useNode, useEditor } from "@craftjs/core";
import { FormControl, FormLabel, Slider, TextField, Select, MenuItem, Checkbox, FormControlLabel, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { useTemplateString } from '../useTemplateString';

const BasicSettings = ({ name, qwantumId, text, setProp, hasContext, itemContext }) => (
  <>
    <FormControl fullWidth margin="normal">
      <FormLabel>Component Name</FormLabel>
      <TextField
        value={name || ''}
        onChange={(e) => setProp(props => props.name = e.target.value)}
        size="small"
        placeholder="Enter component name"
      />
    </FormControl>

    <FormControl fullWidth margin="normal">
      <FormLabel>Quantum ID</FormLabel>
      <TextField
        value={qwantumId || ''}
        onChange={(e) => setProp(props => props.qwantumId = e.target.value)}
        size="small"
        placeholder="Enter quantum ID for persistence"
      />
    </FormControl>

    <FormControl fullWidth margin="normal">
      <FormLabel>Text Content</FormLabel>
      <TextField
        value={text || ''}
        onChange={(e) => setProp(props => props.text = e.target.value)}
        size="small"
        multiline
        rows={2}
        placeholder={hasContext ? "Use ${item.name}, ${item.title}, ${item.description}..." : "Enter text"}
      />
      {hasContext && (
        <div style={{ 
          fontSize: '12px', 
          color: '#2196f3', 
          marginTop: '4px',
          padding: '8px',
          backgroundColor: 'rgba(33, 150, 243, 0.1)',
          borderRadius: '4px',
          border: '1px solid rgba(33, 150, 243, 0.3)'
        }}>
          💡 <strong>Template strings available:</strong><br/>
          <code>${"{item.name}"}</code>, <code>${"{item.title}"}</code>, <code>${"{item.description}"}</code>, <code>${"{item.status}"}</code>, <code>${"{index}"}</code>
          {itemContext && (
            <div style={{ marginTop: '4px' }}>
              <strong>Current item:</strong> {JSON.stringify(itemContext.item, null, 2).substring(0, 100)}...
            </div>
          )}
        </div>
      )}
    </FormControl>
  </>
);

const SuperpowerSettings = ({ currentTime, offspringCards, payable, receivable, balance, stackCount, payables, setProp }) => (
  <FormControl fullWidth margin="normal" component="fieldset">
    <FormLabel component="legend">Superpowers</FormLabel>
    
    <FormControlLabel
      control={
        <Checkbox
          checked={currentTime || false}
          onChange={(e) => setProp(props => props.currentTime = e.target.checked)}
        />
      }
      label="Current Time"
    />

    <FormControlLabel
      control={
        <Checkbox
          checked={offspringCards || false}
          onChange={(e) => setProp(props => props.offspringCards = e.target.checked)}
        />
      }
      label="Offspring Cards"
    />

    <FormControlLabel
      control={
        <Checkbox
          checked={payable || false}
          onChange={(e) => setProp(props => props.payable = e.target.checked)}
        />
      }
      label="Payable"
    />

    <FormControlLabel
      control={
        <Checkbox
          checked={receivable || false}
          onChange={(e) => setProp(props => props.receivable = e.target.checked)}
        />
      }
      label="Receivable"
    />

    <FormControlLabel
      control={
        <Checkbox
          checked={balance || false}
          onChange={(e) => setProp(props => props.balance = e.target.checked)}
        />
      }
      label="Balance"
    />

    <FormControlLabel
      control={
        <Checkbox
          checked={stackCount || false}
          onChange={(e) => setProp(props => props.stackCount = e.target.checked)}
        />
      }
      label="Stack Count"
    />

    <FormControlLabel
      control={
        <Checkbox
          checked={payables || false}
          onChange={(e) => setProp(props => props.payables = e.target.checked)}
        />
      }
      label="Payables"
    />
  </FormControl>
);

const TextStyleSettings = ({ fontSize, fontFamily, fontWeight, fontStyle, textAlign, color, setProp }) => (
  <>
    <FormControl size="small" component="fieldset" fullWidth margin="normal">
      <FormLabel component="legend">Font Size</FormLabel>
      <Slider
        value={fontSize || 16}
        step={1}
        min={8}
        max={72}
        valueLabelDisplay="auto"
        onChange={(_, value) => setProp(props => props.fontSize = value)}
      />
    </FormControl>

    <FormControl fullWidth margin="normal">
      <FormLabel>Font Family</FormLabel>
      <Select
        value={fontFamily || 'Arial'}
        onChange={(e) => setProp(props => props.fontFamily = e.target.value)}
        size="small"
      >
        <MenuItem value="Arial">Arial</MenuItem>
        <MenuItem value="Helvetica">Helvetica</MenuItem>
        <MenuItem value="Montserrat">Montserrat</MenuItem>
        <MenuItem value="Times New Roman">Times New Roman</MenuItem>
        <MenuItem value="Georgia">Georgia</MenuItem>
        <MenuItem value="Verdana">Verdana</MenuItem>
        <MenuItem value="Courier New">Courier New</MenuItem>
        <MenuItem value="Comic Sans MS">Comic Sans MS</MenuItem>
        <MenuItem value="Impact">Impact</MenuItem>
        <MenuItem value="Trebuchet MS">Trebuchet MS</MenuItem>
        <MenuItem value="Arial Black">Arial Black</MenuItem>
      </Select>
    </FormControl>

    <FormControlLabel
      control={
        <Checkbox
          checked={fontWeight === 'bold'}
          onChange={(e) => setProp(props => props.fontWeight = e.target.checked ? 'bold' : 'normal')}
        />
      }
      label="Bold"
    />

    <FormControlLabel
      control={
        <Checkbox
          checked={fontStyle === 'italic'}
          onChange={(e) => setProp(props => props.fontStyle = e.target.checked ? 'italic' : 'normal')}
        />
      }
      label="Italic"
    />

    <FormControl fullWidth margin="normal">
      <FormLabel>Text Alignment</FormLabel>
      <Select
        value={textAlign || 'left'}
        onChange={(e) => setProp(props => props.textAlign = e.target.value)}
        size="small"
      >
        <MenuItem value="left">Left</MenuItem>
        <MenuItem value="center">Center</MenuItem>
        <MenuItem value="right">Right</MenuItem>
        <MenuItem value="justify">Justify</MenuItem>
      </Select>
    </FormControl>

    <FormControl fullWidth margin="normal" component="fieldset">
      <FormLabel component="legend">Text Color</FormLabel>
      <input
        type="color"
        value={color || '#000000'}
        onChange={(e) => setProp(props => props.color = e.target.value)}
      />
    </FormControl>
  </>
);

const AppearanceSettings = ({ cssClass, backgroundColor, borderRadius, padding, setProp }) => (
  <>
    <FormControl fullWidth margin="normal">
      <FormLabel>CSS Class</FormLabel>
      <TextField
        value={cssClass || ''}
        onChange={(e) => setProp(props => props.cssClass = e.target.value)}
        size="small"
        placeholder="Enter CSS class names"
      />
    </FormControl>

    <FormControl fullWidth margin="normal" component="fieldset">
      <FormLabel component="legend">Background Color</FormLabel>
      <input
        type="color"
        value={backgroundColor || '#ffffff'}
        onChange={(e) => setProp(props => props.backgroundColor = e.target.value)}
      />
    </FormControl>

    <FormControl size="small" component="fieldset" fullWidth margin="normal">
      <FormLabel component="legend">Border Radius</FormLabel>
      <Slider
        value={borderRadius || 0}
        step={1}
        min={0}
        max={50}
        valueLabelDisplay="auto"
        onChange={(_, value) => setProp(props => props.borderRadius = value)}
      />
    </FormControl>

    <FormControl size="small" component="fieldset" fullWidth margin="normal">
      <FormLabel component="legend">Padding</FormLabel>
      <Slider
        value={padding || 0}
        step={1}
        min={0}
        max={50}
        valueLabelDisplay="auto"
        onChange={(_, value) => setProp(props => props.padding = value)}
      />
    </FormControl>
  </>
);

const PositionSettings = ({ position, unit, lockedEdges, top, left, right, bottom, setProp }) => (
  <>
    <FormControl fullWidth margin="normal">
      <FormLabel>Position</FormLabel>
      <Select
        value={position || 'relative'}
        onChange={(e) => setProp(props => props.position = e.target.value)}
        size="small"
      >
        <MenuItem value="relative">Relative</MenuItem>
        <MenuItem value="absolute">Absolute</MenuItem>
      </Select>
    </FormControl>

    {(position === 'absolute' || position === 'relative') && (
      <>
        <FormControl fullWidth margin="normal">
          <FormLabel>Unit</FormLabel>
          <ToggleButtonGroup
            value={unit}
            exclusive
            onChange={(e, value) => value && setProp(props => props.unit = value)}
            size="small"
          >
            <ToggleButton value="px">px</ToggleButton>
            <ToggleButton value="%">%</ToggleButton>
          </ToggleButtonGroup>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <FormLabel>Position Controls {position === 'relative' ? '(relative to container)' : '(absolute)'}</FormLabel>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={lockedEdges?.top || false}
                  onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, top: e.target.checked })}
                />
              }
              label="Top"
            />
            <TextField
              value={top || ''}
              onChange={(e) => setProp(props => props.top = parseFloat(e.target.value) || 0)}
              size="small"
              type="number"
              disabled={!lockedEdges?.top}
              style={{ width: '80px' }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={lockedEdges?.left || false}
                  onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, left: e.target.checked })}
                />
              }
              label="Left"
            />
            <TextField
              value={left || ''}
              onChange={(e) => setProp(props => props.left = parseFloat(e.target.value) || 0)}
              size="small"
              type="number"
              disabled={!lockedEdges?.left}
              style={{ width: '80px' }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={lockedEdges?.right || false}
                  onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, right: e.target.checked })}
                />
              }
              label="Right"
            />
            <TextField
              value={right || ''}
              onChange={(e) => setProp(props => props.right = parseFloat(e.target.value) || 0)}
              size="small"
              type="number"
              disabled={!lockedEdges?.right}
              style={{ width: '80px' }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={lockedEdges?.bottom || false}
                  onChange={(e) => setProp(props => props.lockedEdges = { ...props.lockedEdges, bottom: e.target.checked })}
                />
              }
              label="Bottom"
            />
            <TextField
              value={bottom || ''}
              onChange={(e) => setProp(props => props.bottom = parseFloat(e.target.value) || 0)}
              size="small"
              type="number"
              disabled={!lockedEdges?.bottom}
              style={{ width: '80px' }}
            />
          </div>
        </FormControl>
      </>
    )}
  </>
);

export const AggregatorSettings = () => {
  const { 
    actions: { setProp }, 
    name, text, fontSize, fontFamily, fontWeight, fontStyle, textAlign, color,
    position, top, left, right, bottom, unit, lockedEdges,
    backgroundColor, borderRadius, padding, cssClass, qwantumId,
    currentTime, offspringCards, payable, receivable, balance, stackCount, payables,
    parent
  } = useNode((node) => ({
    name: node.data.props.name,
    text: node.data.props.text,
    fontSize: node.data.props.fontSize,
    fontFamily: node.data.props.fontFamily,
    fontWeight: node.data.props.fontWeight,
    fontStyle: node.data.props.fontStyle,
    textAlign: node.data.props.textAlign,
    color: node.data.props.color,
    position: node.data.props.position,
    top: node.data.props.top,
    left: node.data.props.left,
    right: node.data.props.right,
    bottom: node.data.props.bottom,
    unit: node.data.props.unit,
    lockedEdges: node.data.props.lockedEdges,
    backgroundColor: node.data.props.backgroundColor,
    borderRadius: node.data.props.borderRadius,
    padding: node.data.props.padding,
    cssClass: node.data.props.cssClass,
    qwantumId: node.data.props.qwantumId,
    currentTime: node.data.props.currentTime,
    offspringCards: node.data.props.offspringCards,
    payable: node.data.props.payable,
    receivable: node.data.props.receivable,
    balance: node.data.props.balance,
    stackCount: node.data.props.stackCount,
    payables: node.data.props.payables,
    parent: node.data.parent
  }));

  const { hasContext, itemContext } = useTemplateString();
  const { query } = useEditor();
  const parentNode = parent ? query.node(parent).get() : null;

  return (
    <div>
      {parentNode && (
        <FormControl fullWidth margin="normal">
          <FormLabel>Parent: {parentNode.data.displayName || parentNode.data.name || 'Container'}</FormLabel>
          {unit === '%' && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              Note: Parent needs position:relative and defined dimensions for % to work
            </div>
          )}
        </FormControl>
      )}
      
      <BasicSettings 
        name={name}
        qwantumId={qwantumId}
        text={text}
        setProp={setProp}
        hasContext={hasContext}
        itemContext={itemContext}
      />

      <SuperpowerSettings 
        currentTime={currentTime}
        offspringCards={offspringCards}
        payable={payable}
        receivable={receivable}
        balance={balance}
        stackCount={stackCount}
        payables={payables}
        setProp={setProp}
      />

      <TextStyleSettings 
        fontSize={fontSize}
        fontFamily={fontFamily}
        fontWeight={fontWeight}
        fontStyle={fontStyle}
        textAlign={textAlign}
        color={color}
        setProp={setProp}
      />

      <AppearanceSettings 
        cssClass={cssClass}
        backgroundColor={backgroundColor}
        borderRadius={borderRadius}
        padding={padding}
        setProp={setProp}
      />

      <PositionSettings 
        position={position}
        unit={unit}
        lockedEdges={lockedEdges}
        top={top}
        left={left}
        right={right}
        bottom={bottom}
        setProp={setProp}
      />
    </div>
  );
};